import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Grid,
  Chip,
  Icon,
  CircularProgress,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  TextField,
  InputAdornment,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useAuth } from "context/AuthContext";

// Helper to get Pakistan date string (YYYY-MM-DD)
const getPakistanDateString = (dateInput = new Date()) => {
  try {
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Karachi",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    return formatter.format(dateInput);
  } catch (err) {
    return "";
  }
};

// Check if a date/timestamp is today in Pakistan timezone
const isTodayInPakistan = (dateStr) => {
  if (!dateStr || typeof dateStr !== "string") return false;
  const clean = dateStr.trim();
  if (!clean || clean.toLowerCase() === "n/a") return false;

  try {
    const todayPk = getPakistanDateString(); // "YYYY-MM-DD"
    let taskPk = "";

    if (clean.includes("/")) {
      const datePart = clean.split(",")[0].trim();
      const parts = datePart.split("/");
      if (parts.length === 3) {
        const month = parts[0].padStart(2, "0");
        const day = parts[1].padStart(2, "0");
        const year = parts[2];
        taskPk = `${year}-${month}-${day}`;
      }
    }

    if (!taskPk) {
      const d = new Date(clean);
      if (!isNaN(d.getTime())) {
        taskPk = getPakistanDateString(d);
      }
    }

    return todayPk === taskPk;
  } catch (err) {
    return false;
  }
};


// â”€â”€â”€ Kanban Column Config â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const COLUMNS_CONFIG = {
  "To do": {
    color: "#dc2626",
    bgColor: "#fef2f2",
    headerBg: "#fee2e2",
    icon: "assignment_outlined",
  },
  "In progress": {
    color: "#2563eb",
    bgColor: "#eff6ff",
    headerBg: "#dbeafe",
    icon: "hourglass_empty",
  },
  Hold: {
    color: "#d97706",
    bgColor: "#fffbeb",
    headerBg: "#fef3c7",
    icon: "pause_circle_outline",
  },
  Clean: {
    color: "#16a34a",
    bgColor: "#f0fdf4",
    headerBg: "#dcfce7",
    icon: "check_circle_outline",
  },
};

// Context-aware status options per column
const getStatusOptions = (column) => {
  switch (column) {
    case "To do":
      return [
        { value: "not-cleaned", label: "Not Cleaned", column: "To do", color: "#dc2626" },
        { value: "cleaned", label: "Clean", column: "In progress", color: "#2563eb" },
      ];
    case "In progress":
      return [
        { value: "complete", label: "Complete", column: "Clean", color: "#16a34a" },
        { value: "hold", label: "Hold", column: "Hold", color: "#d97706" },
      ];
    case "Hold":
      return [
        { value: "start", label: "Start", column: "In progress", color: "#2563eb" },
      ];
    default:
      return [];
  }
};

// Current display label for a column
const columnLabel = (col) => ({
  "To do": { label: "Not Cleaned", color: "#dc2626", bg: "#fef2f2" },
  "In progress": { label: "Cleaning...", color: "#2563eb", bg: "#eff6ff" },
  "Hold": { label: "On Hold", color: "#d97706", bg: "#fffbeb" },
  "Clean": { label: "Complete", color: "#16a34a", bg: "#f0fdf4" },
})[col] || { label: "Not Cleaned", color: "#dc2626", bg: "#fef2f2" };

// Format seconds → HH:MM:SS
const formatTime = (totalSeconds) => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [h, m, s].map((v) => String(v).padStart(2, "0")).join(":");
};

// Format ISO timestamp to show date and time portion (Pakistan timezone)
const formatFullDateTime = (isoStr) => {
  if (!isoStr) return "N/A";
  try {
    const d = new Date(isoStr);
    return d.toLocaleString("en-US", {
      timeZone: "Asia/Karachi",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true
    });
  } catch (err) {
    return isoStr;
  }
};

// Format ISO timestamp to show only the time portion (excluding date)
const formatTimestamp = (isoStr) => {
  if (!isoStr) return "";
  try {
    const d = new Date(isoStr);
    return d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true
    });
  } catch (err) {
    return isoStr;
  }
};

// Helper to clean trailing Reservation ID or extra bracketed IDs from room names
const cleanRoomName = (roomName) => {
  if (!roomName) return "";
  return roomName.replace(/\s+\([^\)]+\)$/, "").trim();
};

// Helper to extract a sortable timestamp from an apartment object
const getTaskSortTime = (apt) => {
  if (!apt || typeof apt !== "object") return 0;

  const checkoutStr = apt.actualCheckout;
  if (checkoutStr && checkoutStr.trim() !== "" && checkoutStr.trim().toLowerCase() !== "n/a") {
    const clean = checkoutStr.trim();
    const directParsed = Date.parse(clean);
    if (!isNaN(directParsed)) return directParsed;

    const timeMatch = clean.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?/i);
    if (timeMatch) {
      let hours = parseInt(timeMatch[1], 10);
      const minutes = parseInt(timeMatch[2], 10);
      const ampm = timeMatch[4] ? timeMatch[4].toUpperCase() : null;
      if (ampm === "PM" && hours < 12) hours += 12;
      if (ampm === "AM" && hours === 12) hours = 0;
      return hours * 3600 + minutes * 60;
    }
  }

  const timestampStr = apt.timestampToDo || apt.timestamp;
  if (timestampStr) {
    const parsed = Date.parse(timestampStr);
    if (!isNaN(parsed)) return parsed;
  }

  if (apt.reservationId) {
    const resNum = parseInt(apt.reservationId, 10);
    if (!isNaN(resNum)) return resNum;
  }

  return 0;
};

// Helper to extract clean formatted time (e.g. "11:00 AM")
const getTaskFormattedTime = (apt) => {
  if (!apt || typeof apt !== "object") return "";
  const checkoutStr = apt.actualCheckout;
  if (checkoutStr && checkoutStr.trim() !== "" && checkoutStr.trim().toLowerCase() !== "n/a") {
    const clean = checkoutStr.trim();
    const timeMatch = clean.match(/(\d{1,2}:\d{2})(?::\d{2})?\s*(AM|PM)?/i);
    if (timeMatch) {
      const timePart = timeMatch[1];
      const ampm = timeMatch[2] ? ` ${timeMatch[2].toUpperCase()}` : "";
      return `${timePart}${ampm}`;
    }
  }
  return "";
};

// Compute same day checkout labels: returns a Map { aptObject -> { label, num, time } }
const computeSameDayCheckoutLabels = (allApts) => {
  const groups = {};
  allApts.forEach((apt) => {
    if (!apt || typeof apt !== "object") return;
    const name = apt.name;
    if (!name) return;
    const base = cleanRoomName(name).toLowerCase().trim();
    if (!base) return;
    if (!groups[base]) groups[base] = [];
    groups[base].push(apt);
  });

  const labelMap = new Map();

  Object.values(groups).forEach((taskList) => {
    if (taskList.length > 1) {
      const sorted = [...taskList].sort((a, b) => getTaskSortTime(a) - getTaskSortTime(b));
      sorted.forEach((apt, idx) => {
        const num = idx + 1;
        const labelText = `Checkout ${num}`;
        labelMap.set(apt, { label: labelText, num });
      });
    }
  });

  return labelMap;
};


// â”€â”€â”€ Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function CleaningKanban() {
  const { isFullAdmin, isCustom, hasPermission } = useAuth();
  const canEdit = isFullAdmin() || (isCustom() && hasPermission("cleaning", "complete"));

  const [apartments, setApartments] = useState([]);
  const [agentsList, setAgentsList] = useState([]); // populated from Teable
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kanbanMap, setKanbanMap] = useState({});         // { name â†’ column }
  const [agentMap, setAgentMap] = useState({});           // { name â†’ agentName }
  const [timerStartMap, setTimerStartMap] = useState({}); // { name â†’ startTimestamp (ms) }
  const [accumulatedMap, setAccumulatedMap] = useState({}); // { name â†’ seconds accumulated }
  const [elapsedMap, setElapsedMap] = useState({});       // { name â†’ seconds elapsed }
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [notification, setNotification] = useState({ open: false, message: "", severity: "success" });

  // â”€â”€ Refs to always-current state (needed inside callbacks) â”€â”€
  const kanbanMapRef = useRef({});
  const agentMapRef = useRef({});
  const accumulatedMapRef = useRef({});
  useEffect(() => { kanbanMapRef.current = kanbanMap; }, [kanbanMap]);
  useEffect(() => { agentMapRef.current = agentMap; }, [agentMap]);
  useEffect(() => { accumulatedMapRef.current = accumulatedMap; }, [accumulatedMap]);

  // â”€â”€ Save a single apartment's full state to Teable â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const saveToTeable = async (name, overrides = {}) => {
    try {
      const col = overrides.column ?? kanbanMapRef.current[name] ?? "To do";
      const agent = overrides.agent ?? agentMapRef.current[name] ?? "";
      const elapsed = overrides.elapsed ?? accumulatedMapRef.current[name] ?? 0;

      const body = {
        apartmentName: name,
        agent,
        stopwatchTimer: elapsed,
        toDo: col === "To do" ? col : "",
        inProgress: col === "In progress" ? col : "",
        hold: col === "Hold" ? col : "",
        clean: col === "Clean" ? col : "",
        timestamp: new Date().toLocaleString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        }),
      };

      const response = await fetch("/api/cleaning/save-record", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(body),
      });

      const result = await response.json();
      if (!response.ok) {
        console.error("âŒ saveToTeable failed with HTTP status:", response.status, result);
        setNotification({
          open: true,
          message: `Failed to save "${cleanRoomName(name)}" to database (HTTP ${response.status}).`,
          severity: "error",
        });
      } else if (result.action === "skipped") {
        console.warn("âš ï¸ saveToTeable skipped database save (missing Teable configuration on backend):", result.warning);
        setNotification({
          open: true,
          message: `Warning: Task changes not saved to database (Teable configuration missing).`,
          severity: "warning",
        });
      } else {
        console.log("âœ… saveToTeable successfully processed:", result);
      }
    } catch (err) {
      console.error("âš ï¸ saveToTeable network/execution error:", err.message);
      setNotification({
        open: true,
        message: `Network error: Unable to connect to save task.`,
        severity: "error",
      });
    }
  };

  // Live tick for all running timers
  const intervalRef = useRef(null);
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setElapsedMap((prev) => {
        const next = { ...prev };
        Object.keys(timerStartMap).forEach((name) => {
          const startMs = timerStartMap[name];
          const acc = accumulatedMap[name] || 0;
          next[name] = acc + Math.floor((Date.now() - startMs) / 1000);
        });
        return next;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [timerStartMap, accumulatedMap]);

  // â”€â”€ Fetch Agents â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const fetchApartments = async (currentAgents = agentsList) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch("/api/cleaning?kanban=true", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!response.ok) throw new Error("Failed to fetch apartment details from Teable");

      const result = await response.json();

      if (result.success && result.data) {
        const apartmentNames = result.data;
        setApartments(apartmentNames);

        const initialKanbanMap = {};
        const initialAgentMap = {};
        const initialTimerStartMap = {};
        const initialAccumulatedMap = {};
        const initialElapsedMap = {};

        apartmentNames.forEach((apt) => {
          const name = typeof apt === "object" ? apt.name : apt;
          const checkinVal = typeof apt === "object" ? apt.actualCheckin : "";
          const checkoutVal = typeof apt === "object" ? apt.actualCheckout : "";

          // Retrieve last completed checkout time
          const lastCompletedCheckout = localStorage.getItem(`cleaning_kanban_last_completed_checkout_${name}`);

          // Always prioritize database column status as the source of truth
          let savedCol = typeof apt === "object" && apt.column ? apt.column : null;
          if (!savedCol) {
            savedCol = localStorage.getItem(`cleaning_kanban_column_${name}`) || "To do";
          }
          localStorage.setItem(`cleaning_kanban_column_${name}`, savedCol);

          // If the status was completed ("Clean") but a new checkout has arrived,
          // reset status to "To do" and clear the agent and accumulated time in localStorage and Teable
          if (savedCol === "Clean" && checkoutVal && lastCompletedCheckout && checkoutVal !== lastCompletedCheckout) {
            savedCol = "To do";
            localStorage.setItem(`cleaning_kanban_column_${name}`, "To do");
            localStorage.removeItem(`cleaning_kanban_timer_${name}`);
            localStorage.removeItem(`cleaning_kanban_agent_${name}`);
            localStorage.removeItem(`cleaning_kanban_accumulated_${name}`);
            localStorage.removeItem(`cleaning_button_logs_${name}`);

            // Persist the reset to Teable!
            saveToTeable(name, { column: "To do", agent: "", elapsed: 0 });
          } else if (savedCol === "Clean" && checkoutVal && !lastCompletedCheckout) {
            // Initialize lastCompletedCheckout in local storage to prevent false reset on page reload/stale render
            localStorage.setItem(`cleaning_kanban_last_completed_checkout_${name}`, checkoutVal);
          }

          // Restore accumulated seconds from database
          let dbSeconds = 0;
          if (typeof apt === "object" && apt.stopwatchTimer) {
            const parts = apt.stopwatchTimer.split(':');
            if (parts.length === 3) {
              dbSeconds = parseInt(parts[0], 10) * 3600 + parseInt(parts[1], 10) * 60 + parseInt(parts[2], 10);
            }
          }
          localStorage.setItem(`cleaning_kanban_accumulated_${name}`, String(dbSeconds));
          const initialAccumulated = dbSeconds;
          initialAccumulatedMap[name] = initialAccumulated;

          initialKanbanMap[name] =
            savedCol && Object.keys(COLUMNS_CONFIG).includes(savedCol) ? savedCol : "To do";

          // If the status is "Clean", ensure we save the completed checkout time
          if (initialKanbanMap[name] === "Clean" && checkoutVal) {
            localStorage.setItem(`cleaning_kanban_last_completed_checkout_${name}`, checkoutVal);
          }

          // Agent
          let savedAgent = typeof apt === "object" && apt.agent ? apt.agent : "";
          if (savedAgent === "Unassigned") {
            savedAgent = "";
          }
          if (savedAgent) {
            localStorage.setItem(`cleaning_kanban_agent_${name}`, savedAgent);
          } else {
            localStorage.removeItem(`cleaning_kanban_agent_${name}`);
          }
          initialAgentMap[name] = savedAgent;

          // Timer â€” restore if card is in "In progress"
          if (initialKanbanMap[name] === "In progress") {
            const savedStart = localStorage.getItem(`cleaning_kanban_timer_${name}`);
            if (savedStart) {
              const startMs = parseInt(savedStart, 10);
              initialTimerStartMap[name] = startMs;
              initialElapsedMap[name] = initialAccumulated + Math.floor((Date.now() - startMs) / 1000);
            } else {
              // Start a fresh timer (card was in progress but timer not saved)
              const now = Date.now();
              localStorage.setItem(`cleaning_kanban_timer_${name}`, String(now));
              initialTimerStartMap[name] = now;
              initialElapsedMap[name] = initialAccumulated;
            }
          } else {
            // Keep the elapsed time representation frozen for cards not in progress
            initialElapsedMap[name] = initialAccumulated;
          }
        });

        setKanbanMap(initialKanbanMap);
        setAgentMap(initialAgentMap);
        setTimerStartMap(initialTimerStartMap);
        setAccumulatedMap(initialAccumulatedMap);
        setElapsedMap(initialElapsedMap);
      } else {
        setApartments([]);
      }
    } catch (err) {
      console.error("Error loading apartment names:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load agents first, then apartments
  useEffect(() => {
    const init = async () => {
      const res = await fetch("/api/cleaning/agents", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      let liveAgents = [];
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.data && data.data.length > 0) {
          liveAgents = data.data;
          setAgentsList(liveAgents);
        }
      }
      await fetchApartments(liveAgents);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // â”€â”€ Column update â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const updateTaskColumn = async (name, targetColumn, elapsedOverride) => {
    localStorage.setItem(`cleaning_kanban_column_${name}`, targetColumn);
    kanbanMapRef.current = { ...kanbanMapRef.current, [name]: targetColumn };
    setKanbanMap((prev) => ({ ...prev, [name]: targetColumn }));

    if (targetColumn === "Clean") {
      const aptObj = apartments.find((apt) => (typeof apt === "object" ? apt.name : apt) === name);
      if (aptObj && typeof aptObj === "object" && aptObj.actualCheckout) {
        localStorage.setItem(`cleaning_kanban_last_completed_checkout_${name}`, aptObj.actualCheckout);
      }
    }

    // Persist to Teable (column change and optional elapsed override)
    await saveToTeable(name, { column: targetColumn, elapsed: elapsedOverride });
    await fetchApartments(agentsList); // Refetch to get the status transition timestamp immediately!
  };

  // â”€â”€ Agent update â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const updateTaskAgent = async (name, agentName) => {
    localStorage.setItem(`cleaning_kanban_agent_${name}`, agentName);
    agentMapRef.current = { ...agentMapRef.current, [name]: agentName };
    setAgentMap((prev) => ({ ...prev, [name]: agentName }));
    // Persist agent assignment to Teable
    await saveToTeable(name, { agent: agentName });
    await fetchApartments(agentsList);
  };

  // â”€â”€ Timer helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const startTimer = (name) => {
    const now = Date.now();
    localStorage.setItem(`cleaning_kanban_timer_${name}`, String(now));
    setTimerStartMap((prev) => ({ ...prev, [name]: now }));

    // Set elapsed map to the current accumulated value (so it doesn't jump to 0)
    const acc = accumulatedMap[name] || 0;
    setElapsedMap((prev) => ({ ...prev, [name]: acc }));
  };

  const pauseTimer = (name) => {
    const startMs = timerStartMap[name];
    let newAcc = accumulatedMapRef.current[name] || 0;
    if (startMs) {
      const elapsedSession = Math.floor((Date.now() - startMs) / 1000);
      const prevAcc = accumulatedMapRef.current[name] || 0;
      newAcc = prevAcc + elapsedSession;

      localStorage.setItem(`cleaning_kanban_accumulated_${name}`, String(newAcc));
      accumulatedMapRef.current = { ...accumulatedMapRef.current, [name]: newAcc };
      setAccumulatedMap((prev) => ({ ...prev, [name]: newAcc }));
      setElapsedMap((prev) => ({ ...prev, [name]: newAcc }));
    }

    // Stop the active ticking
    localStorage.removeItem(`cleaning_kanban_timer_${name}`);
    setTimerStartMap((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });

    // Persist updated elapsed time to Teable
    saveToTeable(name, { elapsed: newAcc });
  };

  const clearTimer = (name) => {
    localStorage.removeItem(`cleaning_kanban_timer_${name}`);
    localStorage.removeItem(`cleaning_kanban_accumulated_${name}`);

    setTimerStartMap((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
    accumulatedMapRef.current = { ...accumulatedMapRef.current };
    delete accumulatedMapRef.current[name];
    setAccumulatedMap((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
    setElapsedMap((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });

    // Persist cleared timer to Teable
    saveToTeable(name, { elapsed: 0 });
  };


  // Helper to log button press events to local storage
  const logButtonPressEvent = (name, statusValue) => {
    let logs = [];
    try {
      const saved = localStorage.getItem(`cleaning_button_logs_${name}`);
      if (saved) logs = JSON.parse(saved);
    } catch (e) {
      logs = [];
    }

    const now = new Date();
    const formattedTimestamp = now.toLocaleTimeString("en-US", {
      timeZone: 'Asia/Karachi',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });

    if (statusValue === "cleaned") {
      logs.push({ state: "In Progress", timestamp: formattedTimestamp });
    } else if (statusValue === "hold") {
      logs.push({ state: "Hold", timestamp: formattedTimestamp });
    } else if (statusValue === "start") {
      logs.push({ state: "In Progress (resume)", timestamp: formattedTimestamp });
    } else if (statusValue === "complete") {
      logs.push({ state: "Complete", timestamp: formattedTimestamp });
    } else if (statusValue === "not-cleaned") {
      logs = [];
    }

    localStorage.setItem(`cleaning_button_logs_${name}`, JSON.stringify(logs));
  };

  // â”€â”€ Status dropdown handler â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleStatusChange = (name, statusValue) => {
    const currentCol = kanbanMap[name] || "To do";
    const options = getStatusOptions(currentCol);
    const option = options.find((o) => o.value === statusValue);
    if (!option) return;

    const targetColumn = option.column;

    // Check if agent is selected when leaving "To do" / going to "In progress"
    const currentAgent = agentMap[name] || "";
    if (currentCol === "To do" && targetColumn !== "To do" && !currentAgent) {
      const match = name.match(/\s+\((\d+)\)$/);
      const cleanName = match ? name.substring(0, name.length - match[0].length).trim() : name;
      setNotification({
        open: true,
        message: `Please assign an agent to "${cleanName}" first!`,
        severity: "warning",
      });
      return;
    }

    // Log the button press event
    logButtonPressEvent(name, statusValue);

    const wasInProgress = currentCol === "In progress";
    const goingInProgress = targetColumn === "In progress";
    const goingHold = targetColumn === "Hold";
    const goingClean = targetColumn === "Clean";

    let elapsedOverride = undefined;

    if (wasInProgress) {
      if (goingHold || goingClean) {
        // Pause timer: calculate the new accumulated time
        const startMs = timerStartMap[name];
        let newAcc = accumulatedMapRef.current[name] || 0;
        if (startMs) {
          const elapsedSession = Math.floor((Date.now() - startMs) / 1000);
          const prevAcc = accumulatedMapRef.current[name] || 0;
          newAcc = prevAcc + elapsedSession;

          localStorage.setItem(`cleaning_kanban_accumulated_${name}`, String(newAcc));
          accumulatedMapRef.current = { ...accumulatedMapRef.current, [name]: newAcc };
          setAccumulatedMap((prev) => ({ ...prev, [name]: newAcc }));
          setElapsedMap((prev) => ({ ...prev, [name]: newAcc }));
        }

        // Stop the active ticking (remove timer start time)
        localStorage.removeItem(`cleaning_kanban_timer_${name}`);
        setTimerStartMap((prev) => {
          const next = { ...prev };
          delete next[name];
          return next;
        });

        elapsedOverride = newAcc;
      } else {
        // Clear timer
        localStorage.removeItem(`cleaning_kanban_timer_${name}`);
        localStorage.removeItem(`cleaning_kanban_accumulated_${name}`);

        setTimerStartMap((prev) => {
          const next = { ...prev };
          delete next[name];
          return next;
        });
        accumulatedMapRef.current = { ...accumulatedMapRef.current };
        delete accumulatedMapRef.current[name];
        setAccumulatedMap((prev) => {
          const next = { ...prev };
          delete next[name];
          return next;
        });
        setElapsedMap((prev) => {
          const next = { ...prev };
          delete next[name];
          return next;
        });

        elapsedOverride = 0;
      }
    }

    updateTaskColumn(name, targetColumn, elapsedOverride);

    if (goingInProgress) {
      startTimer(name); // start or resume
    }

    const match = name.match(/\s+\((\d+)\)$/);
    const cleanName = match ? name.substring(0, name.length - match[0].length).trim() : name;
    setNotification({
      open: true,
      message: `"${cleanName}" â†’ ${option.label}`,
      severity: "success",
    });
  };

  // â”€â”€ Agent handler â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleAgentSelectChange = (name, selectedAgent) => {
    updateTaskAgent(name, selectedAgent);
    const match = name.match(/\s+\((\d+)\)$/);
    const cleanName = match ? name.substring(0, name.length - match[0].length).trim() : name;
    setNotification({
      open: true,
      message: `Assigned ${selectedAgent} to "${cleanName}"`,
      severity: "success",
    });
  };



  const handleCloseSnackbar = () =>
    setNotification((prev) => ({ ...prev, open: false }));

  // â”€â”€ Loading / Error states â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  if (loading && apartments.length === 0) {
    return (
      <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress color="info" />
        <MDTypography variant="body2" sx={{ ml: 2 }}>Loading apartments...</MDTypography>
      </MDBox>
    );
  }

  if (error) {
    return (
      <Card sx={{ p: 3, m: 2, border: "1px solid #fee2e2", backgroundColor: "#fef2f2" }}>
        <MDTypography color="error" variant="h6">Error Loading Teable Data</MDTypography>
        <MDTypography color="text" variant="body2" sx={{ mt: 1 }}>{error}</MDTypography>
        <MDButton variant="gradient" color="info" size="small" onClick={() => fetchApartments()} sx={{ mt: 2 }}>
          Retry
        </MDButton>
      </Card>
    );
  }

  const baseGrouped = {};
  const seenRoomResKeys = new Set();

  apartments.forEach((apt) => {
    const name = typeof apt === "object" ? apt.name : apt;
    const checkinVal = typeof apt === "object" ? apt.actualCheckin : "";
    const checkoutVal = typeof apt === "object" ? apt.actualCheckout : "";
    const reservationId = typeof apt === "object" ? apt.reservationId : "";
    const hasCheckin = checkinVal && checkinVal.trim() !== "" && checkinVal.trim().toLowerCase() !== "n/a";
    const hasCheckout = checkoutVal && checkoutVal.trim() !== "" && checkoutVal.trim().toLowerCase() !== "n/a";

    const isManualTask = typeof apt === "object" && apt.createdBy && apt.createdBy.trim() !== "";
    const createdTime = typeof apt === "object" ? apt.timestampToDo : "";
    const isCreatedToday = createdTime && isTodayInPakistan(createdTime);

    if (isManualTask) {
      if (!isCreatedToday) return; // Exclude manual tasks not created today
    } else {
      if (!reservationId) return; // Exclude non-manual tasks without reservation ID

      if (!hasCheckin || !hasCheckout) return; // Reservation tasks require both check-in and check-out

      // Check if checkout, departure, or arrival date is today (Pakistan time)
      const isCheckoutToday = typeof apt === "object" && apt.actualCheckout && isTodayInPakistan(apt.actualCheckout);
      const isArrivalToday = typeof apt === "object" && apt.arrivalDate && isTodayInPakistan(apt.arrivalDate);
      const isDepartureToday = typeof apt === "object" && apt.departureDate && isTodayInPakistan(apt.departureDate);

      const isTodayTask = isCheckoutToday || isDepartureToday || isArrivalToday;
      if (!isTodayTask) return; // Strictly exclude tasks from previous/future days
    }

    const baseName = cleanRoomName(name);
    const key = baseName.toLowerCase().trim();

    // Prevent duplicate task cards with identical reservation ID for the same room
    if (reservationId) {
      const resKey = `${key}_res_${reservationId}`;
      if (seenRoomResKeys.has(resKey)) return;
      seenRoomResKeys.add(resKey);
    }

    if (!baseGrouped[key]) {
      baseGrouped[key] = [];
    }
    baseGrouped[key].push(apt);
  });

  const finalVisibleApartments = [];
  Object.values(baseGrouped).forEach((tasks) => {
    if (tasks.length <= 1) {
      finalVisibleApartments.push(tasks[0]);
      return;
    }

    // Sort tasks chronologically ascending: earliest first (Checkout 1 first, Checkout 2 second)
    const sortedTasks = [...tasks].sort((a, b) => getTaskSortTime(a) - getTaskSortTime(b));

    sortedTasks.forEach((apt, idx) => {
      const name = typeof apt === "object" ? apt.name : apt;
      const col = kanbanMap[name] || apt.column || "To do";

      // Check if any LATER task (Checkout 2, etc.) has started or is active
      const hasLaterStartedTask = sortedTasks.slice(idx + 1).some((laterTask) => {
        const laterName = typeof laterTask === "object" ? laterTask.name : laterTask;
        const laterCol = kanbanMap[laterName] || laterTask.column || "To do";
        const laterAgent = agentMap[laterName] || laterTask.agent || "";
        const laterHasAgent = laterAgent.trim() !== "" && laterAgent.trim().toLowerCase() !== "unassigned";
        return laterCol !== "To do" || laterHasAgent;
      });

      // Hide completed Checkout 1 task when Checkout 2 task has started
      if (col === "Clean" && hasLaterStartedTask) {
        return;
      }

      finalVisibleApartments.push(apt);
    });
  });

  const checkoutLabelsMap = computeSameDayCheckoutLabels(finalVisibleApartments);

  const groupedData = { "To do": [], "In progress": [], Hold: [], Clean: [] };
  finalVisibleApartments.forEach((apt) => {
    const name = typeof apt === "object" ? apt.name : apt;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      const rawName = String(name || "").toLowerCase();
      const cleaned = cleanRoomName(name).toLowerCase();
      if (!rawName.includes(q) && !cleaned.includes(q)) {
        return;
      }
    }
    const col = kanbanMap[name] || "To do";
    (groupedData[col] || groupedData["To do"]).push(apt);
  });

  const visibleColumns = Object.keys(COLUMNS_CONFIG).filter((colName) => {
    if (filterStatus === "cleaned") return colName === "Clean";
    if (filterStatus === "not-cleaned") return colName !== "Clean";
    return true;
  });

  // â”€â”€ Render â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  return (
    <MDBox p={1}>
      {/* Header */}
      <MDBox display="flex" justifyContent="flex-end" alignItems="center" flexWrap="wrap" gap={2} mb={{ xs: 2, sm: 4 }}>
        <TextField
          placeholder="Search Room..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon>search</Icon>
              </InputAdornment>
            ),
          }}
          sx={{
            width: { xs: "100%", sm: "280px" },
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              backgroundColor: "#ffffff",
              "& fieldset": {
                borderColor: "#e2e8f0",
              },
              "&:hover fieldset": {
                borderColor: "#cbd5e1",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#000000",
                borderWidth: "1.5px",
              },
            },
          }}
        />
      </MDBox>

      {/* Kanban Grid */}
      <Grid
        container
        spacing={3}
        sx={{
          flexWrap: { xs: "wrap", sm: "nowrap", lg: "wrap" },
          overflowX: { xs: "unset", sm: "auto", lg: "unset" },
          pb: { xs: 0, sm: 2, lg: 0 },
          // Custom premium scrollbar for touch scrolling columns
          "&::-webkit-scrollbar": {
            height: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "rgba(0, 0, 0, 0.02)",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0, 0, 0, 0.15)",
            borderRadius: "10px",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.25)",
            },
          },
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(0, 0, 0, 0.15) rgba(0, 0, 0, 0.02)",
        }}
      >
        {visibleColumns.map((colName) => {
          const colStyle = COLUMNS_CONFIG[colName];
          const colItems = groupedData[colName] || [];

          return (
            <Grid
              item
              xs={12}
              lg={visibleColumns.length === 1 ? 12 : 12 / visibleColumns.length}
              key={colName}
              sx={{
                maxWidth: "100%",
                // Lock column width and prevent shrinking ONLY on tablet portrait (sm) horizontal scroll
                "@media (min-width: 600px) and (max-width: 899.95px)": {
                  flexBasis: visibleColumns.length === 1 ? "100%" : "320px",
                  width: visibleColumns.length === 1 ? "100%" : "320px",
                  flexShrink: 0,
                },
                // Lock column width and prevent shrinking ONLY on tablet landscape / small screens (md) horizontal scroll
                "@media (min-width: 900px) and (max-width: 1199.95px)": {
                  flexBasis: visibleColumns.length === 1 ? "100%" : "340px",
                  width: visibleColumns.length === 1 ? "100%" : "340px",
                  flexShrink: 0,
                },
              }}
            >
              <Card
                sx={{
                  backgroundColor: colStyle.bgColor,
                  border: `1px solid ${colStyle.color}15`,
                  borderRadius: "16px",
                  minHeight: { xs: "480px", md: "550px" },
                  display: "flex",
                  flexDirection: "column",
                  p: { xs: 1.5, sm: 2 },
                  boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
                }}
              >
                {/* Column Header */}
                <MDBox
                  p={{ xs: 1.2, sm: 1.5 }}
                  mb={{ xs: 1.5, sm: 2 }}
                  sx={{
                    backgroundColor: colStyle.headerBg,
                    border: `1px solid ${colStyle.color}20`,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <Icon style={{ color: colStyle.color, fontSize: "20px" }}>{colStyle.icon}</Icon>
                    <MDTypography variant="h6" fontWeight="bold" sx={{ color: colStyle.color, fontSize: "1rem" }}>
                      {colName}
                    </MDTypography>
                  </MDBox>
                  <Chip
                    label={colItems.length}
                    size="small"
                    sx={{ backgroundColor: colStyle.color, color: "#ffffff", fontWeight: "bold", fontSize: "0.75rem" }}
                  />
                </MDBox>

                {/* Cards */}
                <MDBox
                  sx={{
                    flexGrow: 1,
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    maxHeight: { xs: "420px", md: "600px" },
                    pr: 0.5,
                  }}
                >
                  {colItems.length === 0 ? (
                    <MDBox
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        flexGrow: 1,
                        border: "1px dashed rgba(0,0,0,0.05)",
                        borderRadius: "12px",
                        minHeight: "100px",
                      }}
                    >
                      <MDTypography variant="caption" color="text">Empty</MDTypography>
                    </MDBox>
                  ) : (
                    colItems.map((apt) => {
                      const name = typeof apt === "object" ? apt.name : apt;
                      const checkinVal = typeof apt === "object" ? apt.actualCheckin : "";
                      const checkoutVal = typeof apt === "object" ? apt.actualCheckout : "";
                      const checkinEmpty = !checkinVal || checkinVal.trim() === "" || checkinVal.trim().toLowerCase() === "n/a";
                      const checkoutEmpty = !checkoutVal || checkoutVal.trim() === "" || checkoutVal.trim().toLowerCase() === "n/a";
                      const isAvailable = checkinEmpty || !checkoutEmpty;

                      const currentCol = kanbanMap[name] || "To do";
                      const currentAgent = agentMap[name] || "";
                      const isInProgress = currentCol === "In progress";
                      const isClean = currentCol === "Clean";
                      const elapsed = elapsedMap[name] || 0;
                      const availableOpts = getStatusOptions(currentCol);
                      const colMeta = columnLabel(currentCol);
                      // For "To do" the Select value is "not-cleaned"; others use "" with placeholder
                      const selectValue = currentCol === "To do" ? "not-cleaned" : "";

                      const checkoutData = checkoutLabelsMap.get(apt);

                      return (
                        <Card
                          key={name}
                          sx={{
                            p: { xs: 1.5, sm: 2 },
                            backgroundColor: "#ffffff",
                            borderRadius: "12px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                            border: isInProgress
                              ? "1.5px solid #2563eb40"
                              : "1px solid #e2e8f0",
                            display: "flex",
                            flexDirection: "column",
                            gap: { xs: 1, sm: 1.5 },
                            "&:hover": {
                              boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                              transform: "translateY(-2px)",
                              transition: "all 0.2s ease-in-out",
                            },
                          }}
                        >
                          {/* Apartment Name & Times */}
                          <MDBox>
                            <MDTypography
                              variant="subtitle2"
                              fontWeight="bold"
                              sx={{ color: "#1e293b", fontSize: "0.9rem", textAlign: "center", mb: 0.5 }}
                            >
                              {cleanRoomName(name)}
                            </MDTypography>

                            <MDBox display="flex" justifyContent="center" alignItems="center" flexWrap="wrap" gap={1} mb={0.5}>
                              <Chip
                                label={isAvailable ? "Available" : "Reserved"}
                                size="small"
                                sx={{
                                  backgroundColor: isAvailable ? "#f0fdf4" : "#fef2f2",
                                  color: isAvailable ? "#16a34a" : "#dc2626",
                                  border: `1px solid ${isAvailable ? "#86efac" : "#fca5a5"}`,
                                  fontWeight: "bold",
                                  fontSize: "0.68rem",
                                  borderRadius: "6px",
                                  height: "20px",
                                }}
                              />
                              {checkoutData && (
                                <Chip
                                  icon={<Icon style={{ fontSize: "13px", color: checkoutData.num === 1 ? "#1d4ed8" : "#6b21a8" }}>{checkoutData.num === 1 ? "schedule" : "history"}</Icon>}
                                  label={checkoutData.label}
                                  size="small"
                                  sx={{
                                    background: checkoutData.num === 1
                                      ? "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)"
                                      : "linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)",
                                    color: checkoutData.num === 1 ? "#1e40af" : "#6b21a8",
                                    border: `1.5px solid ${checkoutData.num === 1 ? "#93c5fd" : "#c084fc"}`,
                                    fontWeight: 700,
                                    fontSize: "0.68rem",
                                    borderRadius: "8px",
                                    height: "22px",
                                    boxShadow: checkoutData.num === 1
                                      ? "0 2px 5px rgba(37, 99, 235, 0.12)"
                                      : "0 2px 5px rgba(168, 85, 247, 0.12)",
                                    "& .MuiChip-icon": {
                                      ml: 0.8,
                                      mr: -0.4,
                                    },
                                  }}
                                />
                              )}
                            </MDBox>

                            {(checkinVal || checkoutVal || apt.timestampToDo) && (
                              <MDBox
                                display="flex"
                                flexDirection="column"
                                gap={0.5}
                                sx={{
                                  borderTop: "1px dashed #e2e8f0",
                                  pt: 1,
                                  mt: 1,
                                }}
                              >
                                {checkinVal && (
                                  <MDBox display="flex" flexDirection="column" gap={0.2} sx={{ mb: 0.8 }}>
                                    <MDBox display="flex" alignItems="center" gap={0.5}>
                                      <Icon sx={{ color: "#475569", fontSize: "14px !important" }}>login</Icon>
                                      <MDTypography sx={{ color: "#475569", fontSize: { xs: "0.65rem", sm: "0.7rem" }, fontWeight: "bold" }}>
                                        Actual Check in Time:
                                      </MDTypography>
                                    </MDBox>
                                    <MDTypography sx={{ color: "#64748b", fontSize: { xs: "0.65rem", sm: "0.7rem" }, pl: 2.3 }}>
                                      {checkinVal}
                                    </MDTypography>
                                  </MDBox>
                                )}
                                {checkoutVal && (
                                  <MDBox display="flex" flexDirection="column" gap={0.2}>
                                    <MDBox display="flex" alignItems="center" gap={0.5}>
                                      <Icon sx={{ color: "#475569", fontSize: "14px !important" }}>logout</Icon>
                                      <MDTypography sx={{ color: "#475569", fontSize: { xs: "0.65rem", sm: "0.7rem" }, fontWeight: "bold" }}>
                                        Actual Check out Time:
                                      </MDTypography>
                                    </MDBox>
                                    <MDTypography sx={{ color: "#64748b", fontSize: { xs: "0.65rem", sm: "0.7rem" }, pl: 2.3 }}>
                                      {checkoutVal}
                                    </MDTypography>
                                  </MDBox>
                                )}
                                {apt.timestampToDo && (
                                  <MDBox display="flex" flexDirection="column" gap={0.2} sx={{ mt: 0.8 }}>
                                    <MDBox display="flex" alignItems="center" gap={0.5}>
                                      <Icon sx={{ color: "#2563eb", fontSize: "14px !important" }}>access_time</Icon>
                                      <MDTypography sx={{ color: "#475569", fontSize: { xs: "0.65rem", sm: "0.7rem" }, fontWeight: "bold" }}>
                                        Task Created Time:
                                      </MDTypography>
                                    </MDBox>
                                    <MDTypography sx={{ color: "#64748b", fontSize: { xs: "0.65rem", sm: "0.7rem" }, pl: 2.3 }}>
                                      {formatFullDateTime(apt.timestampToDo)}                                    </MDTypography>
                                  </MDBox>
                                )}
                              </MDBox>
                            )}

                            {/* â”€â”€ Chronological Button Activity Logs â”€â”€ */}
                            {currentCol !== "To do" && (() => {
                              let logs = [];
                              try {
                                const saved = localStorage.getItem(`cleaning_button_logs_${name}`);
                                if (saved) logs = JSON.parse(saved);
                              } catch (e) { }

                              if (logs.length === 0) {
                                // Fallback to database timestamps if local logs are empty
                                const dbLogs = [];
                                if (apt.timestampToDo) {
                                  dbLogs.push({ state: "To do", timestamp: formatTimestamp(apt.timestampToDo), raw: apt.timestampToDo });
                                }
                                if (apt.timestampInProgress) {
                                  dbLogs.push({ state: "In Progress", timestamp: formatTimestamp(apt.timestampInProgress), raw: apt.timestampInProgress });
                                }
                                if (apt.timestampHold) {
                                  dbLogs.push({ state: "Hold", timestamp: formatTimestamp(apt.timestampHold), raw: apt.timestampHold });
                                }
                                if (apt.timestampClean) {
                                  dbLogs.push({ state: "Complete", timestamp: formatTimestamp(apt.timestampClean), raw: apt.timestampClean });
                                }
                                // Sort by raw ISO string
                                dbLogs.sort((a, b) => new Date(a.raw) - new Date(b.raw));
                                logs = dbLogs;
                              }

                              if (logs.length === 0) return null;

                              return (
                                <MDBox
                                  display="flex"
                                  flexDirection="column"
                                  gap={0.5}
                                  sx={{
                                    borderTop: "1px dashed #e2e8f0",
                                    pt: 1,
                                    mt: 1,
                                  }}
                                >
                                  <MDTypography sx={{ color: "#475569", fontSize: { xs: "0.65rem", sm: "0.7rem" }, fontWeight: "bold", mb: 0.5 }}>
                                    Activity Logs:
                                  </MDTypography>
                                  {logs.map((log, idx) => (
                                    <MDBox key={idx} display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.2 }}>
                                      <MDTypography sx={{ color: "#475569", fontSize: "0.68rem", fontWeight: "medium" }}>
                                        {log.state}:
                                      </MDTypography>
                                      <MDTypography sx={{ color: "#64748b", fontSize: "0.68rem", ml: "auto", fontFamily: "monospace" }}>
                                        {log.timestamp}
                                      </MDTypography>
                                    </MDBox>
                                  ))}
                                </MDBox>
                              );
                            })()}
                          </MDBox>

                          {/* â”€â”€ Stopwatch (when In Progress, Hold, or Clean) â”€â”€ */}
                          {(isInProgress || currentCol === "Hold" || currentCol === "Clean") && (
                            <MDBox
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              gap={0.5}
                              sx={{
                                backgroundColor: isInProgress ? "#dbeafe" : (currentCol === "Hold" ? "#fffbeb" : "#f0fdf4"),
                                border: isInProgress ? "1px solid #93c5fd" : (currentCol === "Hold" ? "1px solid #fde68a" : "1px solid #bbf7d0"),
                                borderRadius: "8px",
                                py: 0.75,
                                px: 1,
                              }}
                            >
                              <Icon
                                sx={{
                                  color: isInProgress ? "#2563eb" : (currentCol === "Hold" ? "#d97706" : "#16a34a"),
                                  fontSize: "16px !important",
                                  animation: isInProgress ? "pulse 1s infinite" : "none",
                                }}
                              >
                                {currentCol === "Clean" ? "check_circle" : "timer"}
                              </Icon>
                              <MDTypography
                                sx={{
                                  color: isInProgress ? "#1d4ed8" : (currentCol === "Hold" ? "#b45309" : "#15803d"),
                                  fontWeight: "bold",
                                  fontSize: "0.85rem",
                                  fontFamily: "monospace",
                                  letterSpacing: "0.05em",
                                }}
                              >
                                {currentCol === "Clean" ? (
                                  <>
                                    <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Total Time: </Box>
                                    {formatTime(elapsed)}
                                  </>
                                ) : formatTime(elapsed)}
                              </MDTypography>
                            </MDBox>
                          )}

                          {/* â”€â”€ Status Dropdown (context-aware) â”€â”€ */}
                          {isClean ? (
                            // Clean: just show a static badge
                            <MDBox
                              display="flex" alignItems="center" justifyContent="center"
                              sx={{
                                backgroundColor: "#dcfce7",
                                border: "1px solid #86efac",
                                borderRadius: "8px",
                                py: 0.6,
                                px: 1,
                              }}
                            >
                              <MDTypography sx={{ color: "#16a34a", fontWeight: "bold", fontSize: "0.75rem" }}>
                                Complete
                              </MDTypography>
                            </MDBox>
                          ) : (
                            <FormControl size="small" fullWidth variant="outlined">
                              <Select
                                value={selectValue}
                                onChange={(e) => handleStatusChange(name, e.target.value)}
                                disabled={!canEdit}
                                displayEmpty
                                renderValue={() => (
                                  <span style={{ color: colMeta.color, fontWeight: "600", fontSize: "0.75rem" }}>
                                    {colMeta.label}
                                  </span>
                                )}
                                sx={{
                                  fontSize: "0.75rem",
                                  py: 0.5,
                                  backgroundColor: colMeta.bg,
                                  "& .MuiSelect-select": { paddingTop: "6px", paddingBottom: "6px" },
                                }}
                              >
                                {availableOpts.map((opt) => (
                                  <MenuItem
                                    key={opt.value}
                                    value={opt.value}
                                    sx={{ fontSize: "0.8rem", color: opt.color, fontWeight: "500" }}
                                  >
                                    {opt.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}

                          {/* â”€â”€ Agent Dropdown â”€â”€ */}
                          <FormControl size="small" fullWidth variant="outlined">
                            <Select
                              multiple
                              value={currentAgent ? currentAgent.split(", ") : []}
                              onChange={(e) => {
                                const val = typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
                                handleAgentSelectChange(name, val.join(", "));
                              }}
                              disabled={!canEdit || isClean}
                              displayEmpty
                              renderValue={(selected) =>
                                selected && selected.length > 0 ? selected.join(", ") : "Select Agent"
                              }
                              sx={{
                                fontSize: "0.75rem",
                                py: 0.5,
                                backgroundColor: !currentAgent ? "#f1f5f9" : "#eff6ff",
                                color: !currentAgent ? "#94a3b8" : "#1d4ed8",
                                fontWeight: !currentAgent ? "regular" : "medium",
                                "& .MuiSelect-select": { paddingTop: "6px", paddingBottom: "6px" },
                              }}
                            >
                              {agentsList.map((agent) => (
                                <MenuItem key={agent} value={agent} sx={{ fontSize: "0.75rem" }}>
                                  {agent}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Card>
                      );
                    })
                  )}
                </MDBox>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Stopwatch pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>

      {/* Snackbar Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={notification.severity} sx={{ width: "100%" }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </MDBox>
  );
}

export default CleaningKanban;
